import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1704040686428-7534b262d0d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzgzNjk3MTI5fDA&ixlib=rb-4.1.0&q=85";

export default function Signup() {
  const [form, setForm] = useState({
    firm_name: "", gstin: "", address: "",
    owner_name: "", email: "", password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.signup(form);
      // Cookie is set by the signup endpoint. Re-hydrate context.
      await login(form.email, form.password);
      toast.success(`Welcome ${form.owner_name.split(" ")[0]} — your firm is live.`);
      navigate("/home");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-anvi-50" data-testid="signup-page">
      <div className="relative hidden md:block md:w-1/2 min-h-screen">
        <img src={HERO} alt="interior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-anvi-900/90 via-anvi-900/70 to-anvi-terracotta/30" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-anvi-100/70">Onboarding</div>
            <div className="font-heading text-xl mt-1">InteriorOS</div>
          </div>
          <div className="max-w-md">
            <h1 className="font-heading text-5xl leading-[1.05] mb-6">Your studio, on Day 1.</h1>
            <p className="text-anvi-100/85 text-sm leading-relaxed mb-6">
              Set up your firm in under 60 seconds. You get a live CRM, a BOQ builder that computes
              per-item and multiplier pricing, 3-tier estimates, GST-compliant invoicing, site
              execution photos, and a branded quotation PDF — all pre-configured.
            </p>
            <div className="space-y-2 text-sm text-anvi-100/80">
              <div>· One tenant, unlimited team members</div>
              <div>· Pre-loaded with your firm's brand + GSTIN</div>
              <div>· No credit card. No trial expiry.</div>
            </div>
          </div>
          <Link to="/login" className="text-xs tracking-[0.2em] uppercase text-anvi-100/70 hover:text-white flex items-center gap-2" data-testid="back-to-login">
            <ArrowLeft size={12} /> Existing user · Sign in
          </Link>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-xs tracking-[0.25em] uppercase text-anvi-500 mb-3">Firm Onboarding</div>
          <h2 className="font-heading text-4xl text-anvi-900 mb-2">Set up your firm.</h2>
          <p className="text-anvi-500 text-sm mb-6">You'll be the Owner · you can invite team after.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="pt-2 pb-1 text-[10px] tracking-[0.15em] uppercase text-anvi-500">Firm</div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Firm name</label>
              <input required value={form.firm_name} onChange={update("firm_name")} data-testid="signup-firm-name"
                     className="w-full border border-anvi-200 bg-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Address / City</label>
                <input required value={form.address} onChange={update("address")} data-testid="signup-address"
                       placeholder="Nagpur, Maharashtra"
                       className="w-full border border-anvi-200 bg-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
              </div>
              <div>
                <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">GSTIN (optional)</label>
                <input value={form.gstin} onChange={update("gstin")} data-testid="signup-gstin"
                       placeholder="27ABC..." maxLength={15}
                       className="w-full border border-anvi-200 bg-white rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-anvi-terracotta" />
              </div>
            </div>

            <div className="pt-3 pb-1 text-[10px] tracking-[0.15em] uppercase text-anvi-500">Owner account</div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Your name</label>
              <input required value={form.owner_name} onChange={update("owner_name")} data-testid="signup-owner-name"
                     className="w-full border border-anvi-200 bg-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Email</label>
              <input required type="email" value={form.email} onChange={update("email")} data-testid="signup-email"
                     className="w-full border border-anvi-200 bg-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Password (min 6)</label>
              <input required type="password" value={form.password} onChange={update("password")} minLength={6}
                     data-testid="signup-password"
                     className="w-full border border-anvi-200 bg-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>

            <button type="submit" disabled={submitting} data-testid="signup-submit-btn"
                    className="w-full bg-anvi-terracotta hover:bg-anvi-terracotta-dark text-white rounded-md px-4 py-3 font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2 group mt-2">
              {submitting ? "Creating firm…" : (<>Create firm & sign in <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" /></>)}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-anvi-500">
            Already have an account? <Link to="/login" className="text-anvi-terracotta hover:underline" data-testid="signin-link">Sign in →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
